import { toast } from "react-toastify";
import "../styles/Global.css";
import { ErrorOutline, TaskAlt, WarningAmber } from "@mui/icons-material";
import { CourseInstance } from "../model/entity/CourseInstance";
import { TimeSlotDTO } from "../model/entity/TimeSlotDTO";

export const determineHeaderTimes = (arr: CourseInstance[] | TimeSlotDTO[]) => {
  if (arr.length === 0) return { from: 15, to: 21 };

  let timeMin = "15:00";
  let timeMax = "20:00";

  arr.forEach((obj) => {
    const start = obj.startTime.substring(0, 5);
    let end = obj.endTime.substring(0, 5);
    if (end === "00:00") end = "24:00";

    timeMin = start < timeMin ? start : timeMin;
    timeMax = end > timeMax ? end : timeMax;
  });

  const from = parseInt(timeMin.substring(0, 2));
  const to = parseInt(timeMax.substring(0, 2));

  return { from, to: Math.min(24, to + 1) };
};

export const generateTimeSlotOptions = (
  from: number = 0,
  to: number = 24,
  includeLastHour: boolean = false
): string[] => {
  const times: string[] = [];
  for (let hour = from; hour < to; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const formattedTime = `${String(hour).padStart(2, "0")}:${String(
        minute
      ).padStart(2, "0")}`;
      times.push(formattedTime);
    }
  }
  if (includeLastHour) times.push(`${to === 24 ? "00" : to.toString()}:00`);
  return times;
};

export const hexToRgba = (hex: string, opacity: number) => {
  hex = hex.replace(/^#/, "");
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const notifOk = (msg: string, duration: number | null = 3000) => {
  toast.dismiss();
  return toast.success(msg, {
    className: "toast-notification toast-success",
    autoClose: duration === null ? false : duration,
    icon: () => <TaskAlt fontSize="small" sx={{ color: "#15751a" }} />,
  });
};

export const notifErr = (msg: string, duration: number | null = 3000) => {
  toast.dismiss();
  return toast.error(msg, {
    className: "toast-notification toast-error",
    autoClose: duration === null ? false : duration,
    icon: () => <ErrorOutline fontSize="small" sx={{ color: "#cc353d" }} />,
  });
};

export const notifWarn = (msg: string, duration: number | null = 3000) => {
  toast.dismiss();
  return toast.warning(msg, {
    className: "toast-notification toast-warning",
    autoClose: duration === null ? false : duration,
    icon: () => <WarningAmber fontSize="small" sx={{ color: "#ef8801" }} />,
  });
};

// append CZ00 to the end of the bban string, replace letters with their ASCII value
// then modulo this whole number by 97 and subtract the result from 98 to get the check digits
function calculateIbanCheckDigits(countryCode: string, bban: string): string {
  const replaceLetters = (str: string) =>
    str
      .toUpperCase()
      .replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString()); // A=10, B=11...

  const numericIban = replaceLetters(bban + countryCode + "00");
  const remainder = BigInt(numericIban) % BigInt(97);
  const checkDigits = 98 - Number(remainder);
  return checkDigits.toString().padStart(2, "0");
}

// Czech IBAN is 24 digits - CZ|check-digits[2]|bank-code[4]|prefix[6]|account-number[10]
export const convertToIban = (acc: string) => {
  const prefix = acc.includes("-") ? acc.split("-")[0].padStart(6, "0") : "000000";

  const noPrefix = acc.includes("-") ? acc.split("-")[1] : acc;

  const parts = noPrefix.split("/");

  const accountPart = parts[0].padStart(10, "0");
  const bankCodePart = parts[1];
  const bban = `${bankCodePart}${prefix}${accountPart}`;

  const checkDigits = calculateIbanCheckDigits("CZ", `${bban}`);

  return `CZ${checkDigits}${bban}`;
}